import { Navigate } from "react-router-dom";
import { useSupabaseAuth } from "@/contexts/SupabaseAuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCurrentProfile } from "@/hooks/useSupabaseProfile";

/**
 * Defines the props for the ProtectedRoute component.
 * @param {React.ReactNode} children - The child components to render if the user is authenticated.
 * @param {string[]} [roles] - An optional array of roles that are allowed to access the route.
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
  const { isAuthenticated, loading, user } = useSupabaseAuth();
  const { data: userProfile } = useCurrentProfile(user?.id);

  // Show loading indicator while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-purple-600 mx-auto" />
          <p className="mt-4 text-white">Loading application...</p>
        </div>
      </div>
    );
  }

  // Redirect to auth page if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Check for role-based access if roles are specified
  if (roles && roles.includes('admin') && !userProfile?.is_admin) {
    toast.error("You don't have permission to access this page.");
    return <Navigate to="/dashboard" replace />;
  }

  // User is authenticated and has the required role, render children
  return <>{children}</>;
};

export default ProtectedRoute;
